import type React from "react"
import { TShirt } from "@phosphor-icons/react"
import { addWeeks } from "date-fns"
import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { Alert } from "~/components/reusable/Alert"
import { Button } from "~/components/reusable/Button"
import {
  DialogBodyV2,
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import Select from "~/components/reusable/Select"
import {
  useAddVariantsToCollection,
  useCollectionOptions,
  useGetSkuIdsForCollection,
  useMoveVariantsToCollection,
  useNoosCollectionMembership,
} from "~/hooks/useCollections"
import { useSaveMetadata } from "~/hooks/useMetadata"
import { getStartOfCurrentWeekUTC, isMondayDatePicker } from "~/utils/datetime"
import { AddVariantStep } from "./AddVariantsToCollectionDialog"
import HandleAddVariantConflictContent from "./content/HandleAddVariantConflictContent"
import { useVariantsByIds } from "~/hooks/useVariants"
import { api } from "~/utils/api"
import DatePicker from "~/components/reusable/DatePicker"
import { Label } from "~/components/reusable/Label"
import { formatName } from "~/utils/namingUtils"
import { errorMessageIncludes } from "~/utils/errorUtils"

export type NpiVariantData = {
  variantId: string
  variantName: string | null
  skuIds: string[]
  hasSalesBasedForecast: boolean
  activatedFrom: Date | null
  isNpi: boolean
}

type Props = {
  variantIds: string[]
  disabled?: boolean
  disabledTooltip?: string
  npiVariantData?: NpiVariantData[]
  trigger?: React.ReactNode
  productType?: "noos" | "fashion"
  needsActivation?: boolean
  onSuccess?: () => void
}

export const HandleAddToCollectionDialog = ({
  variantIds,
  disabled,
  disabledTooltip,
  npiVariantData,
  trigger: customTrigger,
  productType,
  needsActivation,
  onSuccess: onSuccessProp,
}: Props) => {
  const [open, setOpen] = useState(false)
  const storeVariants = useVariantsByIds(variantIds)
  const hasNpiVariants = useMemo(() => {
    if (needsActivation) return true
    if (npiVariantData && npiVariantData.length > 0) return true
    return storeVariants.some((v) => v.isNpi)
  }, [needsActivation, npiVariantData, storeVariants])

  const { data: selectedSkuIds } = api.sku.getSkuIdsForVariants.useQuery({
    variantIds,
  })
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | undefined>(undefined)
  const defaultDate = useMemo(() => addWeeks(getStartOfCurrentWeekUTC(), 1), [])
  const [date, setDate] = useState<Date>(defaultDate)
  const [variantDates, setVariantDates] = useState<Map<string, Date>>(new Map())
  const [step, setStep] = useState<AddVariantStep>(AddVariantStep.AddVariants)

  const skuIdsAlreadyInCollection = useGetSkuIdsForCollection(selectedCollectionId)
  const allCollectionOptions = useCollectionOptions(undefined, true)
  const collectionOptions = useMemo(() => {
    if (needsActivation) return allCollectionOptions?.filter((o) => o.type === "noos")
    if (productType === "fashion") return allCollectionOptions?.filter((o) => o.type === "mixed")
    return allCollectionOptions
  }, [allCollectionOptions, productType, needsActivation])
  const { mutate: addVariants, isPending: isAddingVariants } = useAddVariantsToCollection()
  const { mutateAsync: saveMetadataAsync, isPending: isSavingMetadata } = useSaveMetadata()

  const remainingSKUs = useMemo(() => {
    if (!selectedSkuIds) return []
    return selectedSkuIds.filter((skuId) => !skuIdsAlreadyInCollection.includes(skuId))
  }, [selectedSkuIds, skuIdsAlreadyInCollection])

  const isNoosCollection = useMemo(() => {
    return (
      collectionOptions?.find((option) => option.value === selectedCollectionId)?.type === "noos"
    )
  }, [selectedCollectionId, collectionOptions])

  const targetName = useMemo(
    () => collectionOptions?.find((option) => option.value === selectedCollectionId)?.label,
    [collectionOptions, selectedCollectionId],
  )

  const { data: noosMembership, isLoading: isLoadingNoosMembership } = useNoosCollectionMembership(
    variantIds,
    open,
  )

  const conflictingNoosByVariant = useMemo(() => {
    const map = new Map<string, { collectionId: number; collectionName: string }[]>()
    if (!isNoosCollection || !noosMembership) return map
    for (const m of noosMembership) {
      if (m.collectionId === selectedCollectionId) continue
      const list = map.get(m.variantId) ?? []
      list.push({ collectionId: m.collectionId, collectionName: m.collectionName })
      map.set(m.variantId, list)
    }
    return map
  }, [noosMembership, isNoosCollection, selectedCollectionId])

  const isSingleVariant = variantIds.length === 1

  const singleVariantNoosConflicts = useMemo(() => {
    const id = isSingleVariant ? variantIds[0] : undefined
    return id ? (conflictingNoosByVariant.get(id) ?? []) : []
  }, [isSingleVariant, variantIds, conflictingNoosByVariant])

  const sourceNoosCollection =
    singleVariantNoosConflicts.length === 1 ? singleVariantNoosConflicts[0] : undefined
  const hasMultipleNoosConflicts = singleVariantNoosConflicts.length > 1
  const hasSingleVariantNoosConflict = singleVariantNoosConflicts.length > 0

  const excludedVariantIds = useMemo(() => {
    if (isSingleVariant) return []
    return variantIds.filter((id) => (conflictingNoosByVariant.get(id)?.length ?? 0) > 0)
  }, [isSingleVariant, variantIds, conflictingNoosByVariant])

  const excludedSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds])

  const addableVariantIds = useMemo(
    () => variantIds.filter((id) => !excludedSet.has(id)),
    [variantIds, excludedSet],
  )

  const { mutate: moveVariants, isPending: isMoving } = useMoveVariantsToCollection()

  const remainingSkuIdsSet = useMemo(() => new Set(remainingSKUs), [remainingSKUs])

  const { forecastVariants, needsActivationVariants } = useMemo(() => {
    if (!npiVariantData) return { forecastVariants: [], needsActivationVariants: [] }
    const forecast: NpiVariantData[] = []
    const needsActivation: NpiVariantData[] = []
    const filterByRemaining = selectedCollectionId != null && remainingSkuIdsSet.size > 0
    for (const v of npiVariantData) {
      if (excludedSet.has(v.variantId)) continue
      if (filterByRemaining) {
        const hasRemaining = v.skuIds.some((id) => remainingSkuIdsSet.has(id))
        if (!hasRemaining) continue
      }
      if (v.hasSalesBasedForecast) {
        forecast.push(v)
      } else {
        needsActivation.push(v)
      }
    }
    return { forecastVariants: forecast, needsActivationVariants: needsActivation }
  }, [npiVariantData, remainingSkuIdsSet, selectedCollectionId, excludedSet])

  const usePerVariantDates = hasNpiVariants && isNoosCollection && !!npiVariantData

  const getVariantDate = useCallback(
    (variantId: string, activatedFrom: Date | null) => {
      return variantDates.get(variantId) ?? activatedFrom ?? defaultDate
    },
    [variantDates, defaultDate],
  )

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedCollectionId(undefined)
      setDate(defaultDate)
      setVariantDates(new Map())
      setStep(AddVariantStep.AddVariants)
    }
    setOpen(open)
  }

  const handleAddSkusError = useCallback(
    (error: { message?: string }) => {
      if (errorMessageIncludes(error, "do not match the specified collection type")) {
        setStep(AddVariantStep.HandleConflict)
      } else if (errorMessageIncludes(error, "already in another NOOS collection")) {
        // hook already toasted; keep dialog open so the user can pick another collection
      } else {
        setOpen(false)
      }
    },
    [setStep],
  )

  const onMove = () => {
    if (!selectedCollectionId || !sourceNoosCollection) return
    moveVariants(
      {
        sourceCollectionId: sourceNoosCollection.collectionId,
        targetCollectionId: Number(selectedCollectionId),
        variantIds,
      },
      {
        onSuccess: () => {
          setOpen(false)
          onSuccessProp?.()
        },
      },
    )
  }

  const onSubmit = () => {
    if (!selectedCollectionId || addableVariantIds.length === 0) return

    if (usePerVariantDates) {
      addVariants(
        {
          collectionId: Number(selectedCollectionId),
          variantIds: addableVariantIds,
        },
        {
          onSuccess: async () => {
            if (needsActivationVariants.length > 0) {
              const dateGroups = new Map<number, string[]>()
              const inCollectionSet = new Set(skuIdsAlreadyInCollection)
              for (const v of needsActivationVariants) {
                const d = getVariantDate(v.variantId, v.activatedFrom)
                const key = d.getTime()
                const existing = dateGroups.get(key) ?? []
                const remainingForVariant = v.skuIds.filter((id) => !inCollectionSet.has(id))
                existing.push(...remainingForVariant)
                dateGroups.set(key, existing)
              }
              const results = await Promise.allSettled(
                [...dateGroups].map(([timestamp, skuIds]) =>
                  saveMetadataAsync({ skuIds, activatedFrom: new Date(timestamp) }),
                ),
              )
              const failedCount = results.filter((r) => r.status === "rejected").length
              if (failedCount > 0) {
                toast.error(`Failed to set activation date for ${failedCount} group(s)`)
              }
              setOpen(false)
              onSuccessProp?.()
            } else {
              setOpen(false)
              onSuccessProp?.()
            }
          },
          onError: handleAddSkusError,
        },
      )
    } else {
      addVariants(
        {
          collectionId: Number(selectedCollectionId),
          variantIds: addableVariantIds,
          startDate: hasNpiVariants ? date : undefined,
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSuccessProp?.()
          },
          onError: handleAddSkusError,
        },
      )
    }
  }

  const variantsAlreadyInCollectionCount = useMemo(() => {
    if (!npiVariantData || skuIdsAlreadyInCollection.length === 0) return 0
    const inCollectionSet = new Set(skuIdsAlreadyInCollection)
    return npiVariantData.filter((v) => v.skuIds.every((id) => inCollectionSet.has(id))).length
  }, [npiVariantData, skuIdsAlreadyInCollection])

  const hasSkuOverlap = useMemo(() => {
    if (npiVariantData) return false // handled by variant count above
    return selectedSkuIds?.some((id) => skuIdsAlreadyInCollection.includes(id)) ?? false
  }, [npiVariantData, selectedSkuIds, skuIdsAlreadyInCollection])

  const remainingVariantCount = variantIds.length - variantsAlreadyInCollectionCount
  const addableVariantCount = Math.max(0, remainingVariantCount - excludedVariantIds.length)

  const defaultTrigger = (
    <Button size="sm" variant="neutral">
      <TShirt />
      Add to collection
    </Button>
  )

  const triggerElement = customTrigger ?? defaultTrigger

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        requires="edit"
        asChild
        disabled={disabled}
        allowedTitle={disabled ? disabledTooltip : undefined}
      >
        {triggerElement}
      </DialogTrigger>
      {step === AddVariantStep.AddVariants && (
        <DialogContentV2>
          <DialogHeaderV2>
            <DialogTitle>
              Add {variantIds.length} variant{variantIds.length !== 1 ? "s" : ""} to collection
            </DialogTitle>
          </DialogHeaderV2>
          <DialogBodyV2>
            {collectionOptions?.length === 0 ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5 text-sm text-fg-subtle">
                  <p className="font-semibold">We couldn&apos;t find any eligible collections</p>
                  <p>This could be due to:</p>
                  <ul className="list-disc pl-5">
                    <li>No collections exist</li>
                    <li>
                      Selected variants don&apos;t match any collection type (e.g. fashion variants
                      require a fashion collection)
                    </li>
                    <li>Only ERP collections are available</li>
                  </ul>
                </div>
              </div>
            ) : (
              <>
                <Select
                  label="Collection"
                  value={collectionOptions?.find((option) => option.value === selectedCollectionId)}
                  onChange={(option) => setSelectedCollectionId(option?.value)}
                  options={collectionOptions}
                  getOptionValue={(option) => option.label}
                  leftIcon={<TShirt />}
                />
                {hasNpiVariants &&
                  (needsActivation || isNoosCollection) &&
                  !npiVariantData &&
                  !hasSingleVariantNoosConflict && (
                    <>
                      <Label>When to start forecasting</Label>
                      <DatePicker
                        filterDate={isMondayDatePicker}
                        value={date}
                        onChange={(day) => {
                          if (day) setDate(day)
                        }}
                        minDate={addWeeks(getStartOfCurrentWeekUTC(), 1)}
                      />
                      <p className="text-sm text-fg-subtle">
                        When activating new SKUs into a collection, you need to provide the date
                        when we should start forecasting the SKUs. This can only be a monday.
                      </p>
                    </>
                  )}
                {usePerVariantDates && !hasSingleVariantNoosConflict && (
                  <div className="flex flex-col gap-3">
                    {forecastVariants.length > 0 && (
                      <p className="text-sm text-fg-subtle">
                        {forecastVariants.length} variant{forecastVariants.length !== 1 ? "s" : ""}{" "}
                        already {forecastVariants.length === 1 ? "has" : "have"} a{" "}
                        {formatName("sale", true)}-based forecast and{" "}
                        {forecastVariants.length === 1 ? "does" : "do"} not need an activation date.
                      </p>
                    )}
                    {needsActivationVariants.length > 0 && (
                      <>
                        <p className="text-sm font-medium">
                          Set activation date per variant ({needsActivationVariants.length})
                        </p>
                        <div className="flex flex-col gap-2 overflow-y-auto max-h-60 py-1">
                          {needsActivationVariants.map((v) => (
                            <div key={v.variantId} className="flex items-center gap-3">
                              <span className="min-w-0 flex-1 truncate text-sm">
                                {v.variantName}
                              </span>
                              <div className="w-[180px] shrink-0">
                                <DatePicker
                                  filterDate={isMondayDatePicker}
                                  value={getVariantDate(v.variantId, v.activatedFrom)}
                                  onChange={(day) => {
                                    if (!day) return
                                    setVariantDates((prev) => {
                                      const next = new Map(prev)
                                      next.set(v.variantId, day)
                                      return next
                                    })
                                  }}
                                  minDate={addWeeks(getStartOfCurrentWeekUTC(), 1)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {variantsAlreadyInCollectionCount > 0 && (
                  <Alert>
                    <p>
                      Out of the <b>{variantIds.length}</b> selected variant
                      {variantIds.length !== 1 ? "s" : ""},{" "}
                      <b>{variantsAlreadyInCollectionCount}</b> are already part of the collection.
                      Only the remaining <b>{addableVariantCount}</b> variant
                      {addableVariantCount !== 1 ? "s" : ""} will be added
                    </p>
                  </Alert>
                )}
                {hasSkuOverlap && (
                  <Alert>
                    <p>Some of the selected variants are already part of this collection</p>
                  </Alert>
                )}
                {sourceNoosCollection && (
                  <Alert>
                    <p>
                      This variant is already in NOOS collection{" "}
                      <b>{sourceNoosCollection.collectionName}</b>. A variant can only be in one
                      NOOS collection, so it will be moved here.
                    </p>
                  </Alert>
                )}
                {hasMultipleNoosConflicts && (
                  <Alert>
                    <p>
                      This variant is in multiple NOOS collections (
                      {singleVariantNoosConflicts.map((c) => c.collectionName).join(", ")}). Remove
                      it from those before adding it here.
                    </p>
                  </Alert>
                )}
                {excludedVariantIds.length > 0 && (
                  <Alert>
                    <p>
                      <b>{excludedVariantIds.length}</b> of the <b>{variantIds.length}</b> selected
                      variants are already in another NOOS collection and will be skipped. The
                      remaining <b>{addableVariantCount}</b> variant
                      {addableVariantCount !== 1 ? "s" : ""} will be added.
                    </p>
                  </Alert>
                )}
                {addableVariantCount > 0 && (
                  <Alert variant="info">
                    <p>
                      Adding to a collection makes the{" "}
                      {addableVariantCount !== 1 ? "products" : "product"} visible and active for
                      planning. {addableVariantCount !== 1 ? "Their forecasts" : "Its forecast"}{" "}
                      will only be generated after the next forecast run.
                    </p>
                  </Alert>
                )}
              </>
            )}
          </DialogBodyV2>
          <DialogFooterV2 className="justify-between">
            <DialogClose asChild>
              <Button variant="transparent">Cancel</Button>
            </DialogClose>
            {sourceNoosCollection ? (
              <Button
                requires="edit"
                onClick={onMove}
                loading={isMoving}
                disabled={!selectedCollectionId}
              >
                {isMoving ? "Moving..." : `Move to ${targetName ?? "collection"}`}
              </Button>
            ) : (
              <Button
                requires="edit"
                onClick={onSubmit}
                loading={isAddingVariants || isSavingMetadata}
                disabled={
                  !selectedCollectionId ||
                  (npiVariantData == null && remainingSKUs.length === 0) ||
                  addableVariantCount === 0 ||
                  hasMultipleNoosConflicts ||
                  isLoadingNoosMembership
                }
              >
                {isAddingVariants || isSavingMetadata
                  ? "Adding variants..."
                  : `Add ${addableVariantCount} variant${addableVariantCount !== 1 ? "s" : ""}`}
              </Button>
            )}
          </DialogFooterV2>
        </DialogContentV2>
      )}
      {step === AddVariantStep.HandleConflict && selectedCollectionId && (
        <HandleAddVariantConflictContent
          variantIds={variantIds}
          collectionId={Number(selectedCollectionId)}
          setOpen={handleOpenChange}
        />
      )}
    </DialogRoot>
  )
}
